import React, { FC } from 'react';
import {
    SubmitErrorHandler,
    SubmitHandler,
    useForm
} from 'react-hook-form/dist/index.ie11';

import {
    Text,
    Heading3,
    Dropdown,
    TextField,
    InputPassword,
    FieldMessage
} from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';

import registrationAndSignInTranslations from 'public/locales/en/registration-and-sign-in.json';
import { getTranslator } from 'shared/i18n/getTranslator';
import useRegistration from './../../hooks/useRegistration';
import RegistrationFormData from './../../interfaces/RegistrationFormData';
import { trackAuthSubmit } from './../../tracking';
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH } from './../../validation/rules';
import { USER_STATUS_KEYS } from './../../constants/userStatusKeys';

import {
    FormSection,
    AboutYouLabel,
    AuthModalSubHeadingWrapper,
    RequiredIndicator,
    ReassuranceText,
    RegisterButton
} from './../../AuthModal.styles';
import validationDecoration from 'shared/helpers/validationDecoration';
import { RadioGroup, RadioButton } from 'shared/components';
import { AuthModalProps } from '../../AuthModal.types';
import {
    transformFormData,
    hasNthErrors
} from 'shared/components/AuthModal/utils';

const translate = getTranslator(registrationAndSignInTranslations);
const serviceError = translate('serviceError');
const detailsHeading = translate('detailsSection.heading');
const required = translate('required');
const detailsEmailFieldLabel = translate('detailsSection.emailFieldLabel');
const detailsEmailFieldValidation = translate(
    'detailsSection.emailFieldValidation'
);
const detailsPasswordFieldLabel = translate(
    'detailsSection.passwordFieldLabel'
);
const detailsPasswordFieldGuidance = translate(
    'detailsSection.passwordFieldGuidance'
);
const aboutYouHeading = translate('aboutYouSection.heading');
const aboutYouLabel = translate('aboutYouSection.aboutYouLabel');
const aboutYouDefaultOption = translate(
    'aboutYouSection.aboutYouDefaultOption'
);
const termsUnsubscribe = translate('terms.youMayUnsubscribe');
const registerButton = translate('registerButton');

const RegisterForm: FC<
    Pick<
        AuthModalProps,
        | 'close'
        | 'actionAfterAuth'
        | 'resetActionAfterAuth'
        | 'isSignInModalEnabled'
    >
> = ({
    close,
    actionAfterAuth,
    resetActionAfterAuth = () => null,
    isSignInModalEnabled
}) => {
    const {
        register,
        handleSubmit,
        errors,
        setError,
        formState,
        reset,
        trigger
    } = useForm<RegistrationFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });

    const { registerUser, generalError } = useRegistration(setError, close);

    const onFormSubmit: SubmitHandler<RegistrationFormData> = async (
        formData
    ) => {
        const payload = await registerUser(transformFormData(formData));
        if (!payload?.data?.registerUser) {
            return;
        }

        if (payload.data.registerUser.success) {
            await actionAfterAuth?.action();
            resetActionAfterAuth();
            close();
        }
        trackAuthSubmit({
            payload: payload.data.registerUser,
            formData,
            eventLabel: actionAfterAuth?.eventLabel
        });
    };
    const onFormSubmitError: SubmitErrorHandler<RegistrationFormData> = (
        errors
    ) => {
        if (
            errors.consent__insights__24 &&
            hasNthErrors({ expectedErrors: 1, formErrors: errors })
        ) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'registration',
                eventAction: 'failure_must_select_opt_in',
                eventLabel: actionAfterAuth?.eventLabel
            });
        }
    };

    const onSubmit = handleSubmit(onFormSubmit, onFormSubmitError);

    React.useEffect(() => {
        // Reset form when Safari / Firefox read the previous page from the Back-Forward Cache
        window.addEventListener('pageshow', function () {
            reset();
        });

        reset();
    }, []);

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            data-testid="auth-modal-registration-form"
        >
            {generalError && (
                <FieldMessage
                    data-testid="general-error"
                    variant="invalid"
                    message={serviceError}
                />
            )}
            <FormSection isSignInModalEnabled={isSignInModalEnabled}>
                <AuthModalSubHeadingWrapper>
                    <Heading3 size="7">{detailsHeading}</Heading3>
                    <Text size="small">
                        <RequiredIndicator>*</RequiredIndicator>
                        {required}
                    </Text>
                </AuthModalSubHeadingWrapper>

                <TextField
                    id="input-email-address"
                    name="email"
                    type="email"
                    autoComplete="new-email"
                    label={detailsEmailFieldLabel}
                    isRequired
                    validationStatus={validationDecoration<RegistrationFormData>(
                        'email',
                        errors,
                        formState.touched
                    )}
                    validationErrorMessage={
                        errors.email &&
                        ((errors.email.message as string) ||
                            detailsEmailFieldValidation)
                    }
                    ref={register({
                        required: true,
                        pattern: EMAIL_REGEX
                    })}
                    data-testid="email-field"
                />
                <InputPassword
                    id="input-password"
                    name="password"
                    autoComplete="new-password"
                    label={detailsPasswordFieldLabel}
                    isRequired
                    helpText={detailsPasswordFieldGuidance}
                    validationStatus={validationDecoration<RegistrationFormData>(
                        'password',
                        errors,
                        formState.touched
                    )}
                    validationErrorMessage={
                        errors.password &&
                        ((errors.password.message as string) ||
                            detailsPasswordFieldGuidance)
                    }
                    ref={register({
                        required: true,
                        minLength: PASSWORD_MIN_LENGTH
                    })}
                    data-testid="password-field"
                />
            </FormSection>

            <FormSection>
                <Heading3 size="7">{aboutYouHeading}</Heading3>
                <AboutYouLabel htmlFor="situation-dropdown">
                    {aboutYouLabel}
                </AboutYouLabel>
                <Dropdown
                    name="situation"
                    id="situation-dropdown"
                    ref={register}
                    data-testid="situation-dropdown"
                >
                    <option value="">{aboutYouDefaultOption}</option>
                    {USER_STATUS_KEYS.map((statusKey) => (
                        <option
                            value={statusKey}
                            key={`situation-${statusKey}`}
                        >
                            {translate(
                                `aboutYouSection.aboutYouOptions.${statusKey}`
                            )}
                        </option>
                    ))}
                </Dropdown>
            </FormSection>
            <FormSection>
                <RadioGroup
                    legend={translate('emailPreferences.legend')}
                    description={translate('emailPreferences.description')}
                    radioGroupName="consent__insights__24"
                    errorMessage={
                        errors.consent__insights__24 &&
                        translate('emailPreferences.errorMessage')
                    }
                    onClick={() => trigger('consent__insights__24')}
                >
                    <RadioButton
                        value="true"
                        id="yes"
                        activeTextColor="white"
                        label={translate('emailPreferences.yes')}
                        ref={register({ required: true })}
                    />
                    <RadioButton
                        value=""
                        id="no"
                        label={translate('emailPreferences.no')}
                        activeTextColor="white"
                        activeBackgroundColor="function-error"
                        ref={register({ required: true })}
                    />
                </RadioGroup>
            </FormSection>
            <ReassuranceText
                size="secondary"
                color="grey-slate"
                isBold
                data-testid="unsubscribe-text"
            >
                {termsUnsubscribe}
            </ReassuranceText>

            <RegisterButton
                variant="primary"
                isFullWidth
                data-testid="register-button"
            >
                {registerButton}
            </RegisterButton>
        </form>
    );
};

export default RegisterForm;
